import {
    AdsenseCell,
    CategoryFilterCell,
    IndexCell,
    RankCell,
    TrafficShare,
    WebsiteTooltipTopCell,
} from "app/components/React/Table/cells";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { translate } from "app/filters/i18nFilter";
import { GRID_UPGRADE_MOCK_VALUE } from "app/pages/sales-intelligence/constants/common";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { IndustryTaxonomyFilterCell } from "../components/IndustryTaxonomyFilterCell/IndustryTaxonomyFilterCell";
import type { CompetitiveColumnType } from "../types/common";
import type { CompetitiveLandscapeIndustryTaxonomy } from "../types/dto";

import { COMPETITIVE_LANDSCAPE_ROUTE_NAME } from "./constants";

export const selectColumn = {
    field: "select",
    fixed: true,
    cellComponent: RowSelectionConsumer,
    sortable: false,
    headerComponent: SelectAllRowsHeaderCellConsumer,
    isResizable: false,
    width: 40,
    visible: true,
    disableHeaderCellHover: true,
};

export type IndustryColumnHandlers = {
    onSelectCategory: (value: string) => void;
    onSelectIndustryTaxonomy: (nodeId: string) => void;
};

/** Both arms occupy this slot, so the picker's index-keyed toggles can find either one. */
export const INDUSTRY_COLUMN_FIELDS = ["Category", "IndustryTaxonomy"];

/**
 * The two classifications share one slot — the backend serves whichever the account is entitled to,
 * so a row never carries both (ADR-0013). Swapped in place rather than inserted: the column picker
 * keys off array index, so an extra column would shift every toggle after it.
 */
export const getIndustryColumn = (
    { onSelectCategory, onSelectIndustryTaxonomy }: IndustryColumnHandlers,
    showsIndustryTaxonomy: boolean,
): CompetitiveColumnType =>
    showsIndustryTaxonomy
        ? {
              minWidth: 304,
              sortable: true,
              field: "IndustryTaxonomy",
              displayName: translate(leadsSearchFiltersI18nKeys.industryTaxonomyName()),
              cellComponent: (props: ITableCellProps<CompetitiveLandscapeIndustryTaxonomy | undefined>) => (
                  <IndustryTaxonomyFilterCell
                      value={props.value}
                      // The backend masks a locked row's `Domain` but not its taxonomy, so the row
                      // itself is what says whether this industry may be shown.
                      isLocked={props.row?.Domain === GRID_UPGRADE_MOCK_VALUE}
                      onItemClick={() => props.value && onSelectIndustryTaxonomy(props.value.Id)}
                  />
              ),
          }
        : {
              minWidth: 304,
              sortable: true,
              field: "Category",
              displayName: translate("analysis.competitors.similar.table.columns.category.title"),
              tooltip: translate("analysis.competitors.similar.table.columns.category.title.tooltip"),
              cellComponent: (props: ITableCellProps) => (
                  <CategoryFilterCell
                      {...props}
                      onItemClick={() => onSelectCategory(props.value)}
                  />
              ),
          };

export const getColumns = (
    handlers: IndustryColumnHandlers,
    selectedSiteLabel: string,
    showsIndustryTaxonomy: boolean,
): CompetitiveColumnType[] =>
    [
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 40,
            isResizable: false,
        },
        {
            minWidth: 304,
            sortable: true,
            field: "Domain",
            showTotalCount: true,
            displayName: translate("analysis.competitors.similar.table.columns.company.title"),
            tooltip: translate("analysis.competitors.similar.table.columns.domain.title.tooltip"),
            cellComponent: (props: ITableCellProps) => (
                <WebsiteTooltipTopCell
                    {...props}
                    row={{
                        ...props.row,
                        url: proNavigator.href(COMPETITIVE_LANDSCAPE_ROUTE_NAME, {
                            ...proNavigator.getParams(),
                            key: props.row.Domain,
                        }),
                    }}
                />
            ),
        },
        getIndustryColumn(handlers, showsIndustryTaxonomy),
        {
            minWidth: 108,
            sortable: true,
            field: "Rank",
            displayName: translate("analysis.competitors.similar.table.columns.rank.title"),
            tooltip: translate("analysis.competitors.similar.table.columns.rank.title.tooltip"),
            cellComponent: RankCell,
            headerComponent: DefaultCellHeaderRightAlign,
            format: "rank",
        },
        {
            width: 180,
            sortable: true,
            field: "Affinity",
            displayName: translate("analysis.competitors.similar.table.columns.share.title"),
            tooltip: translate("analysis.competitors.similar.table.columns.share.title.tooltip", {
                selectedSiteLabel,
            }),
            cellComponent: TrafficShare,
        },
        {
            width: 77,
            field: "HasAdsense",
            displayName: translate("analysis.all.table.columns.googleAds.title"),
            tooltip: translate("analysis.all.table.columns.googleAds.title.tooltip"),
            cellComponent: AdsenseCell,
        },
    ].map((column: CompetitiveColumnType) => {
        return {
            ...column,
            visible: column.visible !== false,
            headerComponent: column.headerComponent || DefaultCellHeader,
            isResizable: column.isResizable !== false,
        };
    });
